/**
 * Dateを文字列に変換する.
 * yyyy: 年
 * MM: 月
 * dd: 日
 * HH: 時間
 * mm: 分
 * ss: 秒
 * SSS: ミリ秒
 * @param date 日時
 * @param format フォーマット
 * @returns 文字列
 */
export const dateFormat = (date: Date, format: string) => {
  let text = format;
  text = text.replace(/yyyy/g, date.getFullYear().toString());
  text = text.replace(/MM/g, `0${date.getMonth() + 1}`.slice(-2));
  text = text.replace(/dd/g, `0${date.getDate()}`.slice(-2));
  text = text.replace(/HH/g, `0${date.getHours()}`.slice(-2));
  text = text.replace(/mm/g, `0${date.getMinutes()}`.slice(-2));
  text = text.replace(/ss/g, `0${date.getSeconds()}`.slice(-2));
  text = text.replace(/SSS/g, `00${date.getMilliseconds()}`.slice(-3));
  return text;
};

/**
 * new Dateの代替.
 * new Dateを使用するとテストでおかしくなるので、この処理を使用すること.
 * @param year 年
 * @param month 月
 * @param date 日
 * @returns Date
 */
export const newDate = (year: number, month: number, date: number) => {
  const d = new Date(Date.UTC(year, month, date));
  return d;
};

/**
 * 日付文字(yyyy-MM-dd)を生成
 * @param date 日付
 * @returns 文字
 */
export const getDateString = (date: Date) => {
  const pad = (str: string) => `0${str}`.slice(-2);
  const year = (date.getFullYear()).toString();
  const month = pad((date.getMonth() + 1).toString());
  const day = pad(date.getDate().toString());
  return `${year}-${month}-${day}`;
};

/**
 * 日付データ「yyyy-MM-dd」からnew Dateする.
 * @param date 日付データ「yyyy-MM-dd」
 * @returns Date
 */
export const newDateFromString = (date: string, monthDelta: number = 0, dayDelta: number = 0) => {
  const tmp = date.split('-');
  const d = new Date(Date.UTC(Number(tmp[0]), Number(tmp[1]) - 1, Number(tmp[2])));
  d.setMonth(d.getMonth() + monthDelta);
  d.setDate(d.getDate() + dayDelta);
  return d;
};

/**
 * 指定した月数だけシフトした年月を取得する
 * @param year 基準の年
 * @param month 基準の月
 * @param dm シフトする月数(正負)
 * @returns 年月
 */
export const getShiftedYearMonth = (year: number, month: number, dm: number) => {
  const dnext = new Date(year, month + dm);
  return {
    /** 年 */
    year: dnext.getFullYear(),
    /** 月 */
    month: dnext.getMonth(),
  };
};

/**
 * 時間データ「HH:mm:ss」を「HH時mm分」に変換する.
 * @param time 時間データ「HH:mm:ss」
 * @returns 「HH時mm分」
 */
export const convertTimeToDisplayString = (time: string) => {
  const tmp = time.split(':');
  return `${tmp[0]}時${tmp[1]}分`;
};

/**
 * 日付データ「yyyy-MM-dd」を「yyyy年MM月dd日」に変換する.
 * @param date 日付データ「yyyy-MM-dd」
 * @returns 「yyyy年MM月dd日」
 */
export const convertDateToDisplayString = (date: string) => {
  const tmp = date.split('-');
  return `${tmp[0]}年${tmp[1]}月${tmp[2]}日`;
};

/**
 * 表示用日時文字列生成
 * @param date 日時
 * @param isShowWeek 週を含めるか
 * @param isShowYear 年を含めるか
 * @returns yyyy年MM月dd日(week)
 */
export const toStringDateDisplay = (date: Date, isShowWeek = true, isShowYear = true) => {
  const y = date.getFullYear();
  const mon = (`00${date.getMonth() + 1}`).slice(-2);
  const d = (`00${date.getDate()}`).slice(-2);
  if (!isShowWeek) {
    if (isShowYear) {
      return `${y}年${mon}月${d}日`;
    }
    return `${mon}月${d}日`;
  }
  const week = ['日', '月', '火', '水', '木', '金', '土'][date.getDay()];
  if (isShowYear) {
    return `${y}年${mon}月${d}日(${week})`;
  }
  return `${mon}月${d}日(${week})`;
};

/**
 * dateを文字列にする
 * @param date 日時
 * @returns yyyy-MM-dd
 */
export const toStringDate = (date: Date) => {
  const y = date.getFullYear();
  const mon = (`00${date.getMonth() + 1}`).slice(-2);
  const d = (`00${date.getDate()}`).slice(-2);
  return `${y}-${mon}-${d}`;
};

/**
 * dateを日時文字列にする
 * @param date 日時
 * @param isShowSeconds 秒も表示するか
 * @returns hh時mm分ss秒
 */
export const toStringTimeDisplay = (date: Date, isShowSeconds = true) => {
  const h = (`00${date.getHours()}`).slice(-2);
  const m = (`00${date.getMinutes()}`).slice(-2);
  if (isShowSeconds) {
    const s = (`00${date.getSeconds()}`).slice(-2);
    return `${h}時${m}分${s}秒`;
  }
  return `${h}時${m}分`;
};

/**
 * 表示用日時の文字列変換
 * @param date 日時
 * @param isShowSeconds 秒を表示するか
 * @returns yyyy-MM-dd hh:mm:ss
 */
export const toStringDateTimeDisplay = (date: Date, isShowSeconds = true) => {
  const ymd = toStringDateDisplay(date);
  const hms = toStringTimeDisplay(date, isShowSeconds);
  return `${ymd} ${hms}`;
};

/**
 * 時間文字列変換
 * @param date 日時
 * @param isShowSeconds 秒を含めるか
 * @returns hh:mm:ss
 */
export const toStringTime = (date: Date, isShowSeconds = true) => {
  const h = (`00${date.getHours()}`).slice(-2);
  const m = (`00${date.getMinutes()}`).slice(-2);
  if (isShowSeconds) {
    const s = (`00${date.getSeconds()}`).slice(-2);
    return `${h}:${m}:${s}`;
  }
  return `${h}:${m}`;
};

/**
 * 日時の文字列変換
 * @param date 日時
 * @returns yyyy-MM-ddThh:mm:ss.zzzZ
 */
export const toStringDateTime = (date: Date) => {
  const ymd = toStringDate(date);
  const hms = toStringTime(date);
  const milli = (`000${date.getTime() % 1000}`).slice(-3);
  return `${ymd}T${hms}.${milli}Z`;
};

/**
 * 現在時刻と異なる最小限の時間を返す
 * @param date 日時
 * @returns 最小限の時間文字列
 */
export const toStringShort = (date: Date) => {
  const now = new Date();
  if (now.toDateString() === date.toDateString()) {
    return toStringTimeDisplay(date, false);
  }
  if (now.getFullYear() === date.getFullYear()) {
    return toStringDateDisplay(date, false, false);
  }
  return toStringDateDisplay(date, false, true);
};

/**
   * 比較日より前かチェックします.
   * @param date 比較日
   * @returns 比較日より前か
   */
export const isPastDate = (date: Date, targetDate: Date) => {
  let valueNum = targetDate.getFullYear() * 10000;
  valueNum += targetDate.getMonth() * 100;
  valueNum += targetDate.getDate();
  let dateNum = date.getFullYear() * 10000;
  dateNum += date.getMonth() * 100;
  dateNum += date.getDate();
  return valueNum < dateNum;
};

/**
 * 作成日時がバージョン変更日時より前かどうか
 * @param createDatetime 作成日時
 * @returns 古いバージョンかどうか
 */
export const isOldVersion = (createDatetime: string, versionIpDate: string) => {
  const createDate = newDateFromString(createDatetime);
  const isOldVersion = createDate <= newDateFromString(versionIpDate);
  return isOldVersion;
};

/**
 * 現時刻をパースする
 * @param time 時間の文字
 * @returns 日時
 */
export const parseTimeByCurrentDate = (time: string, date: Date | undefined = undefined) => {
  const now = date || new Date();
  const tmp = time.split(':');
  const measureDt = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    parseInt(tmp[0], 10),
    parseInt(tmp[1], 10),
    tmp.length > 2 ? parseInt(tmp[2], 10) : 0,
  );
  return measureDt;
};

/**
 * ふたつの時間差を時間分で表示する
 * @param start 開始時間
 * @param end 終了時間
 * @returns HH時間MM分
 */
export const diffHoursMinutes = (start: Date, end: Date) => {
  const diff = end.getTime() - start.getTime();
  const hour = Math.floor(diff / (60 * 60 * 1000));
  const min = ((diff / (60 * 1000)) - hour * 60).toFixed(0);
  return `${hour}時間${min}分`;
};
